import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { InitialState, PageData, PublicSettings } from '@astrologermatch/shared';

// Page data comes from the server: embedded in the HTML on first load (SSR), then fetched from
// /api/v1/page-data on client-side navigation — the same loader either way, so the two can't drift.

export interface PageDataResponse {
  status: number;
  page: PageData;
  redirect?: string;
  settings: PublicSettings;
}

interface PageState {
  settings: PublicSettings;
  page: PageData;
  path: string;
  loading: boolean;
}

const PageContext = createContext<PageState | null>(null);

export async function fetchPageData(path: string): Promise<PageDataResponse> {
  const res = await fetch(`/api/v1/page-data?path=${encodeURIComponent(path)}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok && res.status !== 404) throw new Error(`page-data ${String(res.status)}`);
  return (await res.json()) as PageDataResponse;
}

export function PageProvider({
  initial,
  children,
}: {
  initial: InitialState;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({
    settings: initial.settings,
    page: initial.page,
    path: new URL(initial.url, 'http://x').pathname,
    loading: false,
  });
  const requested = useRef(state.path);

  useEffect(() => {
    const path = location.pathname;
    if (path === requested.current) return;
    requested.current = path;
    setState((s) => ({ ...s, loading: true }));
    fetchPageData(path)
      .then((data) => {
        if (requested.current !== path) return; // a newer navigation won
        if (data.redirect) {
          void navigate(data.redirect, { replace: true });
          return;
        }
        setState({ settings: data.settings, page: data.page, path, loading: false });
        if (!location.hash) window.scrollTo({ top: 0 });
      })
      .catch(() => {
        if (requested.current !== path) return;
        setState((s) => ({ ...s, page: { kind: 'notFound' }, path, loading: false }));
      });
  }, [location.pathname, location.hash, navigate]);

  return <PageContext.Provider value={state}>{children}</PageContext.Provider>;
}

function usePageState(): PageState {
  const ctx = useContext(PageContext);
  if (!ctx) throw new Error('PageProvider missing');
  return ctx;
}

export function usePage(): PageData {
  return usePageState().page;
}

export function useSettings(): PublicSettings {
  return usePageState().settings;
}

export function usePageLoading(): boolean {
  return usePageState().loading;
}
